import { createFileRoute, Link } from "@tanstack/react-router";
import { useReveal } from "@/hooks/use-reveal";
import { SeoAccordion } from "@/components/seo-accordion";
import { DJ_VAL_SECTIONS } from "@/data/seo-blocks";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
export const HERO = "https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=75&auto=format&fit=crop";
const IMG = {
  starry: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1200&q=72",
  garland: "https://images.unsplash.com/photo-1530023367847-a683933f4172?auto=format&fit=crop&w=1200&q=72",
  reception: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=72",
  outdoor: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1200&q=72",
  dance: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=72",
  ceremony: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1200&q=72",
};

const TITLE = "Ciel étoilé mariage : location & pose | DJ Val";
const DESC =
  "Ciel étoilé pour mariage dans le Vaucluse, Luberon, Alpilles, Aix. Voile noir avec 200+ LED scintillantes, pose incluse, rendu féerique au-dessus du dancefloor. Devis 24 h.";
const CANON = "https://dj-val.fr/ciel-etoile-mariage";

export const Route = createFileRoute("/ciel-etoile-mariage")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: metaDesc(DESC) },
      { name: "keywords", content: "ciel étoilé mariage, ciel étoilé Vaucluse, location ciel étoilé Provence, ciel étoilé Avignon, plafond LED mariage" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: metaDesc(DESC) },
      { property: "og:url", content: CANON },
      { property: "og:image", content: HERO },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: CANON },
      { rel: "preload", as: "image", href: HERO, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Location et pose de ciel étoilé pour mariage",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: [
            { "@type": "City", name: "Cavaillon" },
            { "@type": "City", name: "Avignon" },
            { "@type": "AdministrativeArea", name: "Vaucluse" },
            { "@type": "AdministrativeArea", name: "Bouches-du-Rhône" },
          ],
          offers: { "@type": "Offer", priceCurrency: "EUR", price: "390", priceSpecification: { "@type": "PriceSpecification", description: "À partir de 390 € pour 4×4 m, pose comprise" } },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Combien coûte un ciel étoilé pour un mariage ?", acceptedAnswer: { "@type": "Answer", text: "À partir de 390 € pour une surface 4×4 m avec pose comprise. Tarif dégressif pour surfaces 6×6 m, 8×8 m ou installations multi-zones (dancefloor + cocktail)." } },
            { "@type": "Question", name: "Où peut-on installer un ciel étoilé ?", acceptedAnswer: { "@type": "Answer", text: "Sous tente, chapiteau, dans une grange, une orangerie, sous une pergola fermée. Installation possible à hauteur 2,80 m minimum. Idéal au-dessus du dancefloor ou du dîner." } },
            { "@type": "Question", name: "Comment fonctionne un ciel étoilé ?", acceptedAnswer: { "@type": "Answer", text: "Un voile noir tendu équipé de 200+ fibres optiques ou LED scintillantes alimentées par un boîtier discret. Effet scintillement programmable, sans chaleur, sans bruit." } },
            { "@type": "Question", name: "Livrez-vous le ciel étoilé dans le Vaucluse et Provence ?", acceptedAnswer: { "@type": "Answer", text: "Oui dans tout le 84 et le 13 : Avignon, Cavaillon, Aix-en-Provence, Saint-Rémy, Gordes, Luberon, Alpilles. Aucun frais de déplacement dans un rayon de 80 km de Cavaillon." } },
            { "@type": "Question", name: "Peut-on le combiner avec d'autres éclairages ?", acceptedAnswer: { "@type": "Answer", text: "Oui, c'est même recommandé : guirlandes guinguette en façade, lyres LED sur le dancefloor, projecteurs architecturaux. L'ensemble est synchronisé pour un rendu cinématographique." } },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
            { "@type": "ListItem", position: 2, name: "Ciel étoilé mariage", item: CANON },
          ],
        }),
      },
    ],
  }),
  component: CielEtoilePage,
});

function CielEtoilePage() {
  useReveal();

  return (
    <div className="bg-background text-muted-foreground">
      {/* HERO */}
      <section className="on-dark relative min-h-[85vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img src={HERO} alt="Ciel étoilé scintillant au-dessus d'un dancefloor de mariage en Provence" className="h-full w-full object-cover opacity-55 kenburns-img" fetchPriority="high" width={1920} height={1080} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, var(--ink) 0%, transparent 35%, var(--ink) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">Éclairage signature · Mariage Provence</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">Ciel étoilé</span>
            <span className="block font-light gold-text-shine mt-2">pour votre mariage</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">
            Un voile noir parsemé de 200+ étoiles LED scintillantes, suspendu au-dessus du dancefloor ou du dîner. L'effet le plus photogénique du marché, installé en 2 h, sans bruit, sans chaleur. Disponible dans le Vaucluse, Luberon, Alpilles, Aix.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contactez-nous" className="bg-[var(--gold)] text-[var(--ink)] px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
              Demander un devis
            </Link>
            <a href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "ciel_etoile" })} className="border border-[var(--gold-line)] text-foreground px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              07 67 90 86 67
            </a>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-foreground/85 pt-4">À partir de 390 € · pose incluse</p>
        </div>
      </section>
      <ReviewsProof />

      {/* POURQUOI */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Pourquoi un ciel étoilé</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            L'effet « <span className="italic">waouh</span> » garanti
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <p className="text-sm text-muted-foreground font-light mt-6 max-w-2xl mx-auto">
            Le ciel étoilé transforme n'importe quelle tente, grange ou pergola en cocon magique. Vos invités lèvent la tête, sortent leur téléphone — et vos photographes captent les meilleurs portraits de la soirée.
          </p>
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {[
            { t: "200+ étoiles scintillantes", d: "Fibres optiques et LED basse tension, scintillement aléatoire programmable. Aucune chaleur, aucun bruit." },
            { t: "Installation en 2 h", d: "Système modulaire 4×4, 6×6 ou 8×8 m. Fixation discrète sur structure existante, sans perçage." },
            { t: "Photos cinéma", d: "L'éclairage le plus photogénique du marché — premier plan dancefloor, fond étoilé, rendu nuit étoilée de Provence." },
            { t: "Toutes surfaces", d: "Tente, chapiteau, grange, orangerie, pergola fermée. Hauteur minimale 2,80 m, surface jusqu'à 100 m²." },
            { t: "Synchronisable", d: "Intensité et rythme de scintillement réglables, possibilité de couper pendant les discours puis relancer." },
            { t: "Service tout-inclus", d: "Livraison, installation, démontage, assurance RC Pro. Vous n'avez rien à gérer le jour J." },
          ].map((b, i) => (
            <div key={b.t} className="bg-background p-10 sr" data-reveal style={{ transitionDelay: `${i * 60}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{b.t}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* GALERIE */}
      <section className="py-20 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Inspirations</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">
              Mariages <span className="italic">illuminés</span> en Provence
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { src: IMG.starry, alt: "Ciel étoilé LED au-dessus d'un dancefloor de mariage dans le Vaucluse" },
              { src: IMG.garland, alt: "Guirlandes guinguette combinées à un ciel étoilé en Provence" },
              { src: IMG.reception, alt: "Réception de mariage sous voile étoilé dans le Luberon" },
              { src: IMG.outdoor, alt: "Installation ciel étoilé sous chapiteau dans les Alpilles" },
              { src: IMG.dance, alt: "Piste de danse animée sous ciel étoilé scintillant" },
              { src: IMG.ceremony, alt: "Cocktail de mariage éclairé par ciel étoilé et guirlandes" },
            ].map((img, i) => (
              <div key={i} className="group overflow-hidden border border-[var(--gold-line)] sr aspect-[4/3]" data-reveal style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={600} className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TARIFS */}
      <section className="py-24 px-6 max-w-5xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Tarifs transparents</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            3 formats, <span className="italic">pose incluse</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {[
            { size: "4 × 4 m", price: "390 €", desc: "Idéal au-dessus d'une zone dancefloor intime ou d'un coin lounge." },
            { size: "6 × 6 m", price: "590 €", desc: "Le format le plus demandé : couvre un dancefloor classique de 30–60 invités." },
            { size: "8 × 8 m", price: "890 €", desc: "Effet plafond complet pour grandes tentes / orangeries 80–150 invités." },
          ].map((p, i) => (
            <div key={p.size} className="bg-background p-10 sr text-center" data-reveal style={{ transitionDelay: `${i * 60}ms` }}>
              <p className="eyebrow text-[var(--gold)]">{p.size}</p>
              <p className="font-serif-display text-4xl text-foreground italic mt-4">{p.price}</p>
              <span className="gold-rule mx-auto !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{p.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-xs uppercase tracking-[0.2em] text-foreground/85 mt-8">
          Livraison & pose comprises · 80 km autour de Cavaillon
        </p>
      </section>

      {/* COMBINER */}
      <section className="py-20 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-4xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">À combiner avec</p>
          <h2 className="font-serif-display text-3xl text-foreground mt-4">
            Pour un rendu <span className="italic">cinématographique</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/location-materiel/$slug" params={{ slug: "guirlandes-guinguette" }} className="block border border-[var(--gold-line)] bg-background p-6 hover:border-[var(--gold)] transition-colors">
              <p className="font-serif-display text-xl text-foreground">Guirlandes guinguette</p>
              <span className="gold-rule !w-8 my-3 mx-auto" />
              <p className="text-xs text-muted-foreground font-light">Façade ou tonnelle, ambiance chaleureuse au cocktail.</p>
            </Link>
            <Link to="/location-materiel/$slug" params={{ slug: "lyres-led" }} className="block border border-[var(--gold-line)] bg-background p-6 hover:border-[var(--gold)] transition-colors">
              <p className="font-serif-display text-xl text-foreground">Lyres LED</p>
              <span className="gold-rule !w-8 my-3 mx-auto" />
              <p className="text-xs text-muted-foreground font-light">Faisceaux mobiles colorés pour le dancefloor.</p>
            </Link>
            <Link to="/dj-mariage/pack-sono-lumiere" className="block border border-[var(--gold-line)] bg-background p-6 hover:border-[var(--gold)] transition-colors">
              <p className="font-serif-display text-xl text-foreground">Pack sono + lumière</p>
              <span className="gold-rule !w-8 my-3 mx-auto" />
              <p className="text-xs text-muted-foreground font-light">L'ensemble DJ + ambiance lumineuse clé en main.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">Ciel étoilé · ce qu'on me demande</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {[
            { q: "Combien coûte un ciel étoilé pour un mariage ?", a: "À partir de 390 € pour 4×4 m avec pose comprise. Tarif dégressif pour surfaces 6×6 m, 8×8 m ou installations multi-zones (dancefloor + cocktail)." },
            { q: "Où peut-on installer un ciel étoilé ?", a: "Sous tente, chapiteau, dans une grange, une orangerie, sous une pergola fermée. Installation possible à partir de 2,80 m de hauteur. Idéal au-dessus du dancefloor ou du dîner." },
            { q: "Comment fonctionne un ciel étoilé ?", a: "Un voile noir tendu équipé de 200+ fibres optiques ou LED scintillantes alimentées par un boîtier discret. Effet scintillement programmable, sans chaleur, sans bruit." },
            { q: "Livrez-vous le ciel étoilé dans le Vaucluse et Provence ?", a: "Oui dans tout le 84 et le 13 : Avignon, Cavaillon, Aix-en-Provence, Saint-Rémy, Gordes, Luberon, Alpilles. Aucun frais de déplacement dans un rayon de 80 km de Cavaillon." },
            { q: "Peut-on le combiner avec d'autres éclairages ?", a: "Oui, c'est même recommandé : guirlandes guinguette en façade, lyres LED sur le dancefloor, projecteurs architecturaux. L'ensemble est synchronisé pour un rendu cinématographique." },
            { q: "Quand faut-il réserver le ciel étoilé ?", a: "8 à 10 mois à l'avance pour mai–septembre. Le matériel est unique, je ne fais qu'une installation par week-end." },
          ].map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center border-t border-[var(--gold-line)]">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">
            Offrez à vos invités <span className="italic text-[var(--gold)]">le plus beau plafond</span> de leur vie
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
            Réserver mon ciel étoilé
          </Link>
        </div>
      </section>

      <SeoAccordion
        eyebrow="Ciel étoilé mariage"
        title={<>Effet ciel étoilé : <span className="italic">vos questions</span></>}
        intro="Sécurité, installation, lieux compatibles, tarifs : tout savoir sur l'effet ciel étoilé pour un mariage en Provence."
        sections={DJ_VAL_SECTIONS}
      />
    </div>
  );
}
